import loadable from '@loadable/component';
import { useState, useRef } from 'react';

import {
  ScrollPanelWithoutScrollbars,
  Tab,
  TabList,
  TabPanel,
} from '@simplywallst/ui-core';
import { CompanyNoteModalProvider } from '@/components/Note';
import { NarrativeValuator } from '../NarrativeValuator';

import type { NarrativeForm } from '../../utils/schema';
import type { PastAnnualRevenueEarnings } from '../../types';
import type { NarrativeSidebarCompany } from '../NarrativeSidebar/narrativeSidebarFragment';
import type { CalculationBreakDownOutput } from '../Calculators/';

const CompanyInfo = loadable(
  () => import('@/pages/Narrative/components/CompanyInfo')
);
const Notes = loadable(() => import('@/components/MonitoringPanel'), {
  resolveComponent: (c) => c.Notes,
});

type TabPanelProps = 'valuator' | 'companyInfo' | 'notes';

type Props = {
  company: NarrativeSidebarCompany;
  onChange: (values: Partial<NarrativeForm>) => void;
  onSetFairValue: (fairValue: number) => void;
  readOnly: boolean;
  values: NarrativeForm;
  onReset: () => void;
  pastAnnualRevenueEarnings: PastAnnualRevenueEarnings;
  calculationBreakDownOutput: CalculationBreakDownOutput;
  owner: {
    displayName: string;
    publicId: string;
  };
  narrativeType: 'AI' | 'FAIR_VALUE' | 'LEGACY' | 'USER' | 'WARREN' | null;
};

export const NarrativeTabs = ({
  company,
  onChange,
  onSetFairValue,
  readOnly,
  values,
  onReset,
  pastAnnualRevenueEarnings,
  calculationBreakDownOutput,
  owner,
  narrativeType,
}: Props) => {
  const [activeTab, setActiveTab] = useState<TabPanelProps>('valuator');
  const scrollPanelRef = useRef<HTMLDivElement>(null);

  return (
    <div className="pt-4">
      <CompanyNoteModalProvider>
        <TabList
          boxShadowBottom="subtleBorder"
          templateRows="min-content"
          templateColumns={`repeat(3, max-content)`}
          paddingX="x2"
          justifyContent="flex-start"
        >
          <Tab<TabPanelProps>
            styleType="subnav"
            hasPanel
            selected={activeTab === 'valuator'}
            key="valuator"
            value="valuator"
            onClick={() => setActiveTab('valuator')}
          >
            Valuator
          </Tab>
          <Tab<TabPanelProps>
            styleType="subnav"
            hasPanel
            selected={activeTab === 'companyInfo'}
            key="companyInfo"
            value="companyInfo"
            onClick={() => setActiveTab('companyInfo')}
          >
            Company Info
          </Tab>
          <Tab<TabPanelProps>
            styleType="subnav"
            hasPanel
            selected={activeTab === 'notes'}
            key="notes"
            value="notes"
            onClick={() => setActiveTab('notes')}
          >
            Notes
          </Tab>
        </TabList>
        <ScrollPanelWithoutScrollbars overflow="auto" ref={scrollPanelRef}>
          {/* NOTE: 209px is a manual offset to account for all the vertical space */}
          <div className="relative h-min max-h-[calc(100vh-209px)]">
            <TabPanel
              selected={activeTab === 'valuator'}
              value="valuator"
              padding="x2"
            >
              {company && (
                <NarrativeValuator
                  company={company}
                  values={values}
                  onChange={onChange}
                  onSetFairValue={onSetFairValue}
                  readOnly={readOnly}
                  owner={owner}
                  narrativeType={narrativeType}
                  onReset={onReset}
                  pastAnnualRevenueEarnings={pastAnnualRevenueEarnings}
                  calculationBreakDownOutput={calculationBreakDownOutput}
                />
              )}
            </TabPanel>
            <TabPanel
              selected={activeTab === 'companyInfo'}
              value="companyInfo"
              paddingLg="x2"
            >
              {company && <CompanyInfo company={company} />}
            </TabPanel>
            <TabPanel
              selected={activeTab === 'notes'}
              value="notes"
              padding="x2spacePx"
            >
              {company && (
                <Notes
                  uniqueSymbol={company.uniqueSymbol}
                  companyId={company.id}
                />
              )}
            </TabPanel>
          </div>
        </ScrollPanelWithoutScrollbars>
      </CompanyNoteModalProvider>
    </div>
  );
};
